import { AxiosPromise } from 'axios';
import { IReservationsParams } from '@share/common-types';
import { axiosInstance, axiosInstanceB2B, getHeaders } from '@share/utils';
import { Urls } from '@share/constants';
import { isEmpty } from 'lodash';

export const ReservationsAPI = {
  getReservations(params: IReservationsParams): AxiosPromise<any> {
    const { orderBy, sortOrder, page, pageSize, bookingId, propertyName, leadName, bookingType, status } =  params;

    // Same keys, order and output as before for ordinary values (spaces still go out as %20); values are
    // encoded so free text (property or lead name) can't break the query or inject extra parameters.
    const param = (key: string, value: string | number | undefined | null) =>
      (value ? `&${key}=${encodeURIComponent(String(value))}` : '');

    const orderParam = `&orderBy=${encodeURIComponent(orderBy || 'createdAt')}&sortOrder=${encodeURIComponent(sortOrder || 'desc')}`;
    const queryString = `?page=${page}&results=${pageSize}${param('bookingType', bookingType)}${orderParam}`
      + `${param('bookingId', bookingId)}${param('propertyName', propertyName)}${param('leadName', leadName)}${param('status', status)}`;

    return axiosInstance.get(`${Urls.getReservations}${queryString}`, {
      ...getHeaders(),
    });
  },

  /** Full booking detail by guid. B2B API, not the BFF. */
  getBookingById(bookingId: string): AxiosPromise<any> {
    return axiosInstanceB2B.get(`${Urls.BookingById}/${bookingId}`);
  },

  getStatic(id: number): AxiosPromise<any> {
    return axiosInstance.get(`${Urls.getContentByPropertyId}${`?propertyId=${id}`}`, {
      ...getHeaders(),
    });
  },

  searchByText(queryString: string, type?: string): AxiosPromise<any> {
    const params: any = { queryString };
    if (!isEmpty(type)) {
      params.bookingType = type;
    }
    return axiosInstance.get(Urls.reservationsSearchText, {
      params,
      ...getHeaders(),
    });
  },

  cancelBookingAsync(bookingId: string, comment: string): AxiosPromise<any> {
    return axiosInstance.post(Urls.reservationsCancel, { id: bookingId, comment }, {
      ...getHeaders(),
    });
  },

  cancelBookingEmailAsync(bookingId: string): AxiosPromise<any> {
    return axiosInstance.post(Urls.reservationsCancel, { id: bookingId }, {
      ...getHeaders(),
    });
  },

  // Activity vouchers (TRAV23 activities-front merge). Two separate documents: the
  // customer-facing voucher and the one the supplier requires on arrival.
  downloadActivityVoucherAsync(bookingId: string): Promise<void> {
    return axiosInstanceB2B
      .get(`${Urls.DownloadVoucher}/${bookingId}/voucher`, { responseType: 'blob' })
      .then((response) => {
        const headerLine = response.headers['content-disposition'];
        let filename: string;
        if (headerLine) {
          const startFileNameIndex = headerLine.indexOf('"') + 1;
          const endFileNameIndex = headerLine.lastIndexOf('"');
          filename = headerLine.substring(startFileNameIndex, endFileNameIndex);
        } else {
          filename = `voucher_${bookingId}.pdf`;
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
  },

  downloadSupplierVoucherAsync(bookingId: string): Promise<void> {
    return axiosInstanceB2B
      .get(`${Urls.DownloadVoucher}/${bookingId}/supplier-voucher`, { responseType: 'blob' })
      .then((response) => {
        const headerLine = response.headers['content-disposition'];
        let filename: string;
        if (headerLine) {
          const startFileNameIndex = headerLine.indexOf('"') + 1;
          const endFileNameIndex = headerLine.lastIndexOf('"');
          filename = headerLine.substring(startFileNameIndex, endFileNameIndex);
        } else {
          filename = `supplier_voucher_${bookingId}.pdf`;
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
  },

  voucherBookingAsync(bookingId: string, recepientEmail: string, additionalEmail: string): AxiosPromise<any> {
    return axiosInstance.post(Urls.reservationsSendVoucher, { bookingId, recepientEmail, additionalEmail }, {
      ...getHeaders(),
    });
  },

  downloadbookingVoucherAsync(bookingId: string) {  
    return axiosInstanceB2B
      .get(`${Urls.DownloadVoucher}/${bookingId}/voucher`, {responseType: 'blob'})
      .then((response) => {   
  
        const headerLine = response.headers['content-disposition'];
        let filename;
        if (headerLine) {
          let startFileNameIndex = headerLine.indexOf('"') + 1
          let endFileNameIndex = headerLine.lastIndexOf('"');
          filename = headerLine.substring(startFileNameIndex, endFileNameIndex);
        } else {
          filename = `booking_${bookingId}_voucher.pdf`;
        }
  
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
      });
  },
};
